import React from "react";
import { REFERRAL_HOW_IT_WORKS_CONSTANTS } from "./ReferralHowItWorks.constants";
import { HowItWorksCheckIcon } from "./HowItWorksCheckIcon";

const StepCard = ({ stepNumber, title, description }) => (
  <div className="tw-bg-[#FAFAFD] tw-border tw-border-solid tw-border-[#EEECF5] tw-rounded-[16px] tw-p-[28px_24px] tw-flex tw-flex-col tw-h-full">
    <div className="tw-w-8 tw-h-8 tw-rounded-full tw-bg-[#4E39DF] tw-text-white tw-flex tw-items-center tw-justify-center tw-font-semibold tw-text-sm tw-mb-4">
      {stepNumber}
    </div>
    <h3 className="tw-text-base md:tw-text-lg tw-font-bold tw-text-slate-900 tw-m-0 tw-mb-1">
      {title}
    </h3>
    <p className="tw-text-sm tw-text-slate-500 tw-leading-relaxed tw-m-0">
      {description}
    </p>
  </div>
);

const FriendBenefitsCard = ({ title, items }) => (
  <div className="tw-bg-[#FAFAFD] tw-border tw-border-solid tw-border-[#EEECF5] tw-rounded-[16px] tw-p-[30px_28px] tw-h-full tw-flex tw-flex-col tw-justify-center">
    <h3 className="tw-text-lg tw-font-bold tw-text-slate-900 tw-m-0 tw-mb-5">
      {title}
    </h3>
    <ul className="tw-list-none tw-p-0 tw-m-0 tw-space-y-3.5">
      {items.map((item) => (
        <li
          key={item.ID}
          className="tw-flex tw-items-start tw-gap-3 tw-text-sm tw-text-slate-700"
        >
          <HowItWorksCheckIcon />
          <span className="tw-leading-normal">{item.TEXT}</span>
        </li>
      ))}
    </ul>
  </div>
);

const YourSideCard = ({ title, description, variant }) => {
  const isPrimary = variant === "primary";
  return (
    <div
      className={`tw-rounded-xl tw-p-5 md:tw-p-6 ${
        isPrimary
          ? "tw-bg-[#F1EFFD]"
          : "tw-bg-[#F8F9FB] tw-border tw-border-solid tw-border-slate-100"
      }`}
    >
      <h4
        className={`tw-text-sm md:tw-text-base tw-font-bold tw-m-0 tw-mb-1.5 ${
          isPrimary ? "tw-text-[#4E39DF]" : "tw-text-slate-900"
        }`}
      >
        {title}
      </h4>
      <p className="tw-text-xs md:tw-text-sm tw-text-slate-600 tw-leading-relaxed tw-m-0">
        {description}
      </p>
    </div>
  );
};

export const ReferralHowItWorks = () => {
  const { SECTION_ID, SUBTITLE, TITLE, STEPS, FRIEND_BENEFITS, YOUR_SIDE } =
    REFERRAL_HOW_IT_WORKS_CONSTANTS;

  return (
    <section
      id={SECTION_ID}
      className="tw-w-full tw-my-10 tw-py-16 tw-border-t tw-border-b tw-border-l-0 tw-border-r-0 tw-border-solid tw-border-slate-200 tw-scroll-mt-6"
    >
      {/* Header */}
      <div className="tw-text-center tw-mb-8">
        <span className="tw-text-xs tw-font-bold tw-tracking-wider tw-text-[#4E39DF] tw-uppercase tw-block tw-mb-2">
          {SUBTITLE}
        </span>
        <h2 className="tw-text-2xl md:tw-text-3xl tw-font-extrabold tw-text-slate-900 tw-m-0">
          {TITLE}
        </h2>
      </div>

      {/* 3 Step Cards */}
      <div className="tw-grid tw-grid-cols-1 md:tw-grid-cols-3 tw-gap-4 md:tw-gap-5 tw-mb-6">
        {STEPS.map((step) => (
          <StepCard
            key={step.STEP_NUMBER}
            stepNumber={step.STEP_NUMBER}
            title={step.TITLE}
            description={step.DESCRIPTION}
          />
        ))}
      </div>

      {/* Benefits Breakdown Row */}
      <div className="tw-grid tw-grid-cols-1 md:tw-grid-cols-2 tw-gap-4 md:tw-gap-5">
        {/* Left: What your friend gets */}
        <FriendBenefitsCard
          title={FRIEND_BENEFITS.TITLE}
          items={FRIEND_BENEFITS.ITEMS}
        />

        {/* Right: Your Side */}
        <div className="tw-flex tw-flex-col">
          <span className="tw-text-xs tw-font-bold tw-tracking-wider tw-text-slate-400 tw-uppercase tw-block tw-mb-2.5">
            {YOUR_SIDE.SECTION_LABEL}
          </span>
          <div className="tw-flex tw-flex-col tw-gap-3.5 tw-flex-1">
            {YOUR_SIDE.CARDS.map((card) => (
              <YourSideCard
                key={card.ID}
                title={card.TITLE}
                description={card.DESCRIPTION}
                variant={card.VARIANT}
              />
            ))}
          </div>
        </div>
      </div>
    </section>
  );
};
